import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiArrowLeft } from "react-icons/hi";
import {
  endOfMonth,
  endOfYear,
  format,
  startOfMonth,
  startOfYear,
  subMonths,
  subYears,
} from "date-fns";
import Dashboard from "../../../common/components/layout/Dashboard";
import { useCompanyStore } from "@/common/states/store/Company";
import TrafficFinesSummaryCards from "./components/dashboard/TrafficFinesSummaryCards";
import {
  EMPTY_MANAGEMENT_TRAFFIC_FINES_STATUS_COUNTER,
  EMPTY_TRAFFIC_FINES_STATUS_COUNTER,
  getTrafficFinesDashboardSummary,
  ManagementTrafficFinesStatusCounter,
  TrafficFinesDashboardDateRange,
  TrafficFinesDashboardScope,
} from "./trafficFinesDashboardSummary";
import { StatusCounterFinesReport } from "../../../types/trafficfine.type";

type TrafficFinesDashboardPeriodPreset =
  | "all"
  | "currentYear"
  | "currentMonth"
  | "lastMonth"
  | "lastYear"
  | "custom";

function formatInputDate(date: Date) {
  return format(date, "yyyy-MM-dd");
}

function formatDisplayDate(date: string) {
  if (!date) return "--/--/----";
  const [year, month, day] = date.split("-");
  return `${day}/${month}/${year}`;
}

function getCurrentMonthRange(): TrafficFinesDashboardDateRange {
  const now = new Date();

  return {
    startDate: formatInputDate(startOfMonth(now)),
    endDate: formatInputDate(endOfMonth(now)),
  };
}

function getLastMonthRange(): TrafficFinesDashboardDateRange {
  const lastMonth = subMonths(new Date(), 1);

  return {
    startDate: formatInputDate(startOfMonth(lastMonth)),
    endDate: formatInputDate(endOfMonth(lastMonth)),
  };
}

function getCurrentYearRange(): TrafficFinesDashboardDateRange {
  const now = new Date();

  return {
    startDate: formatInputDate(startOfYear(now)),
    endDate: formatInputDate(endOfYear(now)),
  };
}

function getLastYearRange(): TrafficFinesDashboardDateRange {
  const lastYear = subYears(new Date(), 1);

  return {
    startDate: formatInputDate(startOfYear(lastYear)),
    endDate: formatInputDate(endOfYear(lastYear)),
  };
}

function getRangeByPreset(
  preset: Exclude<TrafficFinesDashboardPeriodPreset, "custom" | "all">,
): TrafficFinesDashboardDateRange {
  if (preset === "currentYear") return getCurrentYearRange();
  if (preset === "currentMonth") return getCurrentMonthRange();
  if (preset === "lastMonth") return getLastMonthRange();
  if (preset === "lastYear") return getLastYearRange();
  return getCurrentYearRange();
}

function TrafficFinesDashboardPage() {
  const navigate = useNavigate();
  const companyStore = useCompanyStore();
  const [statusCounter, setStatusCounter] = useState<StatusCounterFinesReport>(
    EMPTY_TRAFFIC_FINES_STATUS_COUNTER,
  );
  const [managementStatusCounter, setManagementStatusCounter] =
    useState<ManagementTrafficFinesStatusCounter>(
      EMPTY_MANAGEMENT_TRAFFIC_FINES_STATUS_COUNTER,
    );
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPreset, setSelectedPreset] =
    useState<TrafficFinesDashboardPeriodPreset>("all");
  const [selectedScope, setSelectedScope] =
    useState<TrafficFinesDashboardScope>("all");
  const [dateRange, setDateRange] =
    useState<TrafficFinesDashboardDateRange>({ startDate: "", endDate: "" });

  useEffect(() => {
    (async () => {
      if (
        selectedPreset === "custom" &&
        (!dateRange.startDate || !dateRange.endDate)
      ) {
        setStatusCounter(EMPTY_TRAFFIC_FINES_STATUS_COUNTER);
        setManagementStatusCounter(
          EMPTY_MANAGEMENT_TRAFFIC_FINES_STATUS_COUNTER,
        );
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const result = await getTrafficFinesDashboardSummary(
        dateRange,
        selectedScope,
      );

      if (result.ok) {
        setStatusCounter(result.statusCounter);
        setManagementStatusCounter(result.managementStatusCounter);
      } else {
        setStatusCounter(EMPTY_TRAFFIC_FINES_STATUS_COUNTER);
        setManagementStatusCounter(
          EMPTY_MANAGEMENT_TRAFFIC_FINES_STATUS_COUNTER,
        );
      }

      setIsLoading(false);
    })();
  }, [companyStore, dateRange, selectedScope]);

  const handlePresetChange = (
    preset: TrafficFinesDashboardPeriodPreset,
  ) => {
    setSelectedPreset(preset);

    if (preset === "custom") {
      return;
    }

    if (preset === "all") {
      setDateRange({ startDate: "", endDate: "" });
      return;
    }

    setDateRange(getRangeByPreset(preset));
  };

  const handleStartDateChange = (startDate: string) => {
    setSelectedPreset("custom");
    setDateRange((prev) => ({
      startDate,
      endDate:
        prev.endDate && prev.endDate < startDate ? startDate : prev.endDate,
    }));
  };

  const handleEndDateChange = (endDate: string) => {
    setSelectedPreset("custom");
    setDateRange((prev) => ({
      startDate:
        prev.startDate && prev.startDate > endDate ? endDate : prev.startDate,
      endDate,
    }));
  };

  const presetOptions: Array<{
    label: string;
    value: TrafficFinesDashboardPeriodPreset;
  }> = [
    { label: "Tudo", value: "all" },
    { label: "Ano atual", value: "currentYear" },
    { label: "Mês atual", value: "currentMonth" },
    { label: "Mês passado", value: "lastMonth" },
    { label: "Ano passado", value: "lastYear" },
    { label: "Período específico", value: "custom" },
  ];

  const periodLabel =
    selectedPreset === "all"
      ? "Todo o período"
      : `${formatDisplayDate(dateRange.startDate)} até ${formatDisplayDate(dateRange.endDate)}`;

  const scopeOptions: Array<{
    label: string;
    value: TrafficFinesDashboardScope;
  }> = [
    { label: "Todos", value: "all" },
    { label: "Com gestão de multas", value: "management_enabled" },
    { label: "Motos Locavibe", value: "locavibe" },
  ];

  return (
    <Dashboard>
      <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/relatorios/multas")}
                className="p-2 rounded-lg hover:bg-gray-100 transition cursor-pointer"
                title="Voltar"
              >
                <HiArrowLeft className="text-xl text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  Relatório de Multas
                </h1>
                <p className="text-sm text-gray-500">
                  Visão consolidada das infrações e cobranças.
                </p>
              </div>
            </div>

            <div className="flex flex-col items-end gap-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                {presetOptions.map((option) => {
                  const isSelected = selectedPreset === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handlePresetChange(option.value)}
                      className="rounded-full border px-3 py-1.5 text-xs font-medium transition cursor-pointer"
                      style={{
                        borderColor: isSelected ? "rgb(228,65,49)" : "#d1d5db",
                        backgroundColor: isSelected ? "rgb(254,242,242)" : "#fff",
                        color: isSelected ? "rgb(185,28,28)" : "#374151",
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400">
                {periodLabel}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 pl-0 sm:pl-12">
            <span className="text-sm font-medium text-gray-500">Escopo:</span>
            <div className="flex items-center gap-2 flex-wrap">
              {scopeOptions.map((option) => {
                const isSelected = selectedScope === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSelectedScope(option.value)}
                    className="rounded-full border px-3 py-1.5 text-xs font-medium transition cursor-pointer"
                    style={{
                      borderColor: isSelected ? "rgb(228,65,49)" : "#d1d5db",
                      backgroundColor: isSelected ? "rgb(254,242,242)" : "#fff",
                      color: isSelected ? "rgb(185,28,28)" : "#374151",
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {selectedPreset === "custom" && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 pl-0 sm:pl-12">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[rgb(228,65,49)]"
              />
              <span className="text-gray-400 text-sm">até</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => handleEndDateChange(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[rgb(228,65,49)]"
              />
            </div>
          )}
        </div>

        {isLoading ? (
          <div
            className="flex min-h-[min(420px,calc(100vh-14rem))] w-full flex-col items-center justify-center gap-4 py-12"
            aria-busy="true"
            aria-live="polite"
          >
            <div
              className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-[rgb(228,65,49)]"
              role="status"
              aria-label="Carregando"
            />
            <p className="text-sm text-gray-500">Carregando dados...</p>
          </div>
        ) : (
          <TrafficFinesSummaryCards
            statusCounter={statusCounter}
            managementStatusCounter={managementStatusCounter}
          />
        )}
      </div>
    </Dashboard>
  );
}

export default TrafficFinesDashboardPage;
